import { z } from 'zod';

/**
 * Which repositories a custom agent runs on, shared by cubic.yaml and the dashboard.
 *
 * Pure (no server imports) so the dashboard can match and label scopes in the browser.
 */

/**
 * A `pattern` is a repository name, a full `owner/name`, or a wildcard pattern over either: `*`
 * matches any characters and `?` matches one. cubic.yaml only writes patterns. A `repositoryId` entry names one repository by id, so a repository picked in
 * the dashboard stays matched after a rename.
 */
export const RepositoryScopeEntrySchema = z.union([
  z.object({ pattern: z.string() }),
  z.object({ repositoryId: z.number().int() }),
]);

/**
 * `only: null` means every repository, including ones added later; `only: []` means none.
 * A repository matched by `exclude` is out of scope even when `only` matches it.
 */
export const RuleRepositoryScopeSchema = z.object({
  only: z.array(RepositoryScopeEntrySchema).nullable(),
  exclude: z.array(RepositoryScopeEntrySchema),
});

export type RepositoryScopeEntry = z.infer<typeof RepositoryScopeEntrySchema>;
export type RuleRepositoryScope = z.infer<typeof RuleRepositoryScopeSchema>;

/** Most entries a dashboard scope list holds. */
export const REPOSITORY_SCOPE_MAX_ENTRIES = 100;
/** Longest pattern after trimming, the same limit as auto-approve filter values. */
export const REPOSITORY_SCOPE_PATTERN_MAX_LENGTH = 256;
/** Why a dashboard scope with nothing picked can't be saved, in the dialog and from the API. */
export const REPOSITORY_SCOPE_EMPTY_ONLY_ERROR =
  'Select at least one repository or pattern, or choose All repositories';

const RepositoryScopeEntryInputSchema = z.union([
  z.object({ pattern: z.string().trim().min(1).max(REPOSITORY_SCOPE_PATTERN_MAX_LENGTH) }).strict(),
  z.object({ repositoryId: z.number().int().positive() }).strict(),
]);

/**
 * A scope saved from the dashboard. Unlike cubic.yaml, an empty `only` list is rejected:
 * an agent that runs nowhere should be turned off instead.
 */
export const RepositoryScopeInputSchema = z
  .object({
    only: z.array(RepositoryScopeEntryInputSchema).max(REPOSITORY_SCOPE_MAX_ENTRIES).nullable(),
    exclude: z.array(RepositoryScopeEntryInputSchema).max(REPOSITORY_SCOPE_MAX_ENTRIES),
  })
  .strict()
  .refine((scope) => scope.only === null || scope.only.length > 0, {
    message: REPOSITORY_SCOPE_EMPTY_ONLY_ERROR,
    path: ['only'],
  });

function scopeEntryKey(entry: RepositoryScopeEntry): string {
  return 'repositoryId' in entry
    ? `id:${entry.repositoryId}`
    : `pattern:${entry.pattern.trim().toLowerCase()}`;
}

function dedupeScopeEntries(entries: RepositoryScopeEntry[]): RepositoryScopeEntry[] {
  const seen = new Set<string>();
  const deduped: RepositoryScopeEntry[] = [];

  for (const entry of entries) {
    const key = scopeEntryKey(entry);
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    deduped.push('pattern' in entry ? { pattern: entry.pattern.trim() } : entry);
  }

  return deduped;
}

/** Trims patterns and drops repeated entries (patterns compare without case), keeping order. */
export function normalizeRepositoryScope(scope: RuleRepositoryScope): RuleRepositoryScope {
  return {
    only: scope.only === null ? null : dedupeScopeEntries(scope.only),
    exclude: dedupeScopeEntries(scope.exclude),
  };
}

/** The repository a scope is checked against. Without an `id`, id entries never match it. */
export type ScopedRepository = {
  id?: number;
  owner: string;
  name: string;
};

/**
 * Whether `text` matches `pattern`, where `*` matches any run of characters and `?` matches one
 * character. Every other character, including `!`, `#`, `[` and `{`, matches itself.
 *
 * The time this takes grows with the pattern's length times the text's. A glob library compiles
 * patterns to backtracking regular expressions instead, and a short pattern like `*?*?*?*?*?*?*?X`
 * takes seconds to reject one repository name, so a saved scope could stall every review.
 */
function matchesWildcardPattern(pattern: string, text: string): boolean {
  let patternIndex = 0;
  let textIndex = 0;
  // The last `*` seen, and where in the text the characters it matches end.
  let starIndex = -1;
  let starTextIndex = 0;

  while (textIndex < text.length) {
    const patternChar = pattern[patternIndex];
    if (patternChar === '*') {
      starIndex = patternIndex;
      starTextIndex = textIndex;
      patternIndex += 1;
    } else if (patternChar === '?' || patternChar === text[textIndex]) {
      patternIndex += 1;
      textIndex += 1;
    } else if (starIndex !== -1) {
      // Let the last `*` match one more character and retry the rest of the pattern after it.
      patternIndex = starIndex + 1;
      starTextIndex += 1;
      textIndex = starTextIndex;
    } else {
      return false;
    }
  }

  while (pattern[patternIndex] === '*') {
    patternIndex += 1;
  }
  return patternIndex === pattern.length;
}

/**
 * A pattern matches the bare repository name or its full `owner/name`, ignoring case, as GitHub
 * does. Wildcards never match across the `/`, so `acme/*` matches every repository of `acme`.
 */
function matchesRepositoryPattern(pattern: string, repository: ScopedRepository): boolean {
  const trimmedPattern = pattern.trim().toLowerCase();
  if (!trimmedPattern) {
    return false;
  }

  const patternSegments = trimmedPattern.split('/');
  const candidates = [[repository.name], [repository.owner, repository.name]];
  return candidates.some(
    (segments) =>
      segments.length === patternSegments.length &&
      segments.every((segment, index) =>
        matchesWildcardPattern(patternSegments[index] ?? '', segment.toLowerCase()),
      ),
  );
}

export function matchesRepositoryScopeEntry(
  entry: RepositoryScopeEntry,
  repository: ScopedRepository,
): boolean {
  if ('repositoryId' in entry) {
    return repository.id !== undefined && entry.repositoryId === repository.id;
  }

  return matchesRepositoryPattern(entry.pattern, repository);
}

export function isRepositoryInScope(
  scope: RuleRepositoryScope,
  repository: ScopedRepository,
): boolean {
  if (scope.exclude.some((entry) => matchesRepositoryScopeEntry(entry, repository))) {
    return false;
  }

  if (scope.only === null) {
    return true;
  }

  return scope.only.some((entry) => matchesRepositoryScopeEntry(entry, repository));
}

/** True when the scope places no limit at all, so the agent runs on every repository. */
export function appliesToEveryRepository(scope: RuleRepositoryScope): boolean {
  return scope.only === null && scope.exclude.length === 0;
}

/**
 * Proves pattern coverage using only identical segments, universal segments, and literals.
 * Literal inference is bounded to prevent quadratic wildcard matching on large YAML inputs.
 */
function repositoryPatternCovers(excluded: string, included: string): boolean {
  const segments = (pattern: string) => {
    const parts = pattern.trim().toLowerCase().replace(/\*+/g, '*').split('/');
    return parts.length === 1 ? ['*', ...parts] : parts;
  };
  const excludedSegments = segments(excluded);
  const includedSegments = segments(included);
  if (excludedSegments.length !== 2 || includedSegments.length !== 2) {
    return false;
  }

  return includedSegments.every((segment, index) => {
    const excludedSegment = excludedSegments[index] ?? '';
    return (
      excludedSegment === segment ||
      excludedSegment === '*' ||
      (segment.length <= REPOSITORY_SCOPE_PATTERN_MAX_LENGTH &&
        !segment.includes('*') &&
        !segment.includes('?') &&
        matchesWildcardPattern(excludedSegment, segment))
    );
  });
}

/**
 * True when the scope provably can't match any repository. Coverage is conservative, not an
 * exhaustive analysis of wildcard languages: partial overlaps don't prove an empty scope.
 */
export function appliesToNoRepository(scope: RuleRepositoryScope): boolean {
  if (scope.only?.length === 0) {
    return true;
  }
  if (
    scope.exclude.some((entry) => 'pattern' in entry && repositoryPatternCovers(entry.pattern, '*'))
  ) {
    return true;
  }

  return (
    scope.only !== null &&
    scope.only.every((included) =>
      scope.exclude.some((excluded) => {
        if ('repositoryId' in included && 'repositoryId' in excluded) {
          return included.repositoryId === excluded.repositoryId;
        }
        return (
          'pattern' in included &&
          'pattern' in excluded &&
          repositoryPatternCovers(excluded.pattern, included.pattern)
        );
      }),
    )
  );
}

/**
 * Returns a repository's display name for an id entry, or undefined when the viewer can't see
 * that repository. Unresolved entries are counted as hidden rather than shown.
 */
export type RepositoryNameResolver = (repositoryId: number) => string | undefined;

/**
 * Names repositories picked by id: first from `knownNames`, such as the names the server sent with
 * an agent, then from `repositories` the viewer can list. A repository neither knows is hidden
 * from the viewer.
 */
export function createRepositoryNameResolver(
  knownNames: Record<string, string> | undefined,
  repositories: readonly { id: number; name: string }[] = [],
): RepositoryNameResolver {
  const listedNames = new Map(repositories.map(({ id, name }) => [id, name]));
  return (repositoryId) => knownNames?.[String(repositoryId)] ?? listedNames.get(repositoryId);
}

export type RepositoryScopeEntryLabels = {
  names: string[];
  hiddenCount: number;
};

export function labelRepositoryScopeEntries(
  entries: RepositoryScopeEntry[],
  resolveRepositoryName?: RepositoryNameResolver,
): RepositoryScopeEntryLabels {
  const names: string[] = [];
  let hiddenCount = 0;

  for (const entry of entries) {
    if ('pattern' in entry) {
      names.push(entry.pattern);
      continue;
    }

    const name = resolveRepositoryName?.(entry.repositoryId);
    if (name) {
      names.push(name);
    } else {
      hiddenCount += 1;
    }
  }

  return { names, hiddenCount };
}

/** "web-*, design-system, +2 hidden". */
export function formatEntryList({ names, hiddenCount }: RepositoryScopeEntryLabels): string {
  return [...names, ...(hiddenCount > 0 ? [`+${hiddenCount} hidden`] : [])].join(', ');
}

/**
 * Short label for where an agent runs, such as "All repositories",
 * "Only web-*, design-system · except web-legacy" or "All except legacy-monolith".
 */
export function describeRepositoryScope(
  scope: RuleRepositoryScope,
  resolveRepositoryName?: RepositoryNameResolver,
): string {
  const exclude = labelRepositoryScopeEntries(scope.exclude, resolveRepositoryName);
  const excludeText = formatEntryList(exclude);

  if (scope.only === null) {
    return excludeText ? `All except ${excludeText}` : 'All repositories';
  }

  if (scope.only.length === 0) {
    return 'No repositories';
  }

  const onlyText = formatEntryList(labelRepositoryScopeEntries(scope.only, resolveRepositoryName));
  return excludeText ? `Only ${onlyText} · except ${excludeText}` : `Only ${onlyText}`;
}
